import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Camera, Search, ChevronDown } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { uploadPrivate } from '../lib/storage'
import PrivateImage from '../components/PrivateImage'
import { useAuth } from '../context/AuthContext'

const GARMENT_TYPES = ['suit','jacket','trouser','shirt','tuxedo','coat','blazer','shorts','other']
const CURRENCIES = ['USD','GBP','EUR','THB','MYR','AUD','CAD','SGD','AED','NOK','SEK','CHF']
const STATUSES = [
  { value: 'measurements_taken', label: 'Measurements Taken' },
  { value: 'order_confirmed', label: 'Order Confirmed' },
  { value: 'new_lead', label: 'New Lead' },
]

export default function NewOrder() {
  const navigate = useNavigate()
  const { profile } = useAuth()
  const [saving, setSaving] = useState(false)
  const [clientSearch, setClientSearch] = useState('')
  const [clientResults, setClientResults] = useState([])
  const [selectedClient, setSelectedClient] = useState(null)
  const [fabricSearch, setFabricSearch] = useState('')
  const [fabricResults, setFabricResults] = useState([])
  const [selectedFabric, setSelectedFabric] = useState(null)
  const [styleImageUrl, setStyleImageUrl] = useState('')
  const [fabricImageUrl, setFabricImageUrl] = useState('')
  const [scanUrl, setScanUrl] = useState('')
  const [uploadingStyle, setUploadingStyle] = useState(false)
  const [uploadingFabric, setUploadingFabric] = useState(false)
  const [uploadingScan, setUploadingScan] = useState(false)

  const [form, setForm] = useState({
    garment_type: 'suit',
    quantity: 1,
    description: '',
    fabric_notes: '',
    lining_notes: '',
    style_notes: '',
    status: 'measurements_taken',
    order_date: new Date().toISOString().split('T')[0],
    needed_by: '',
    expected_delivery: '',
    trunk_show_city: '',
    price: '',
    currency: 'USD',
    deposit_paid: '',
    notes: '',
  })

  // Client search
  useEffect(() => {
    if (!clientSearch || clientSearch.length < 2) { setClientResults([]); return }
    const timer = setTimeout(async () => {
      const { data } = await supabase.from('clients').select('id, first_name, last_name, email, phone')
        .or(`first_name.ilike.%${clientSearch}%,last_name.ilike.%${clientSearch}%,email.ilike.%${clientSearch}%`)
        .limit(5)
      setClientResults(data || [])
    }, 300)
    return () => clearTimeout(timer)
  }, [clientSearch])

  // Fabric search
  useEffect(() => {
    if (!fabricSearch || fabricSearch.length < 2) { setFabricResults([]); return }
    const timer = setTimeout(async () => {
      const { data } = await supabase.from('fabrics')
        .select('id, fabric_code, name, category, color, supplier, image_url')
        .or(`fabric_code.ilike.%${fabricSearch}%,name.ilike.%${fabricSearch}%,color.ilike.%${fabricSearch}%,supplier.ilike.%${fabricSearch}%`)
        .limit(6)
      setFabricResults(data || [])
    }, 300)
    return () => clearTimeout(timer)
  }, [fabricSearch])

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  async function uploadImage(file, bucket, setUrl, setUploading) {
    if (!file) return
    setUploading(true)
    const ext = file.name.split('.').pop()
    const path = `${Date.now()}.${ext}`
    try {
      setUrl(await uploadPrivate(bucket, path, file))
    } catch (err) {
      alert('Upload failed: ' + err.message)
    }
    setUploading(false)
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setSaving(true)

    const payload = {
      ...form,
      quantity: Number(form.quantity) || 1,
      price: form.price ? Number(form.price) : null,
      deposit_paid: form.deposit_paid ? Number(form.deposit_paid) : null,
      client_id: selectedClient?.id || null,
      fabric_id: selectedFabric?.id || null,
      fabric_image_url: fabricImageUrl || null,
      style_image_url: styleImageUrl || null,
      handwritten_scan_url: scanUrl || null,
      taken_by: profile?.id || null,
      needed_by: form.needed_by || null,
      expected_delivery: form.expected_delivery || null,
    }

    const { data, error } = await supabase.from('orders').insert(payload).select().single()
    setSaving(false)
    if (!error) navigate(`/orders/${data.id}`)
    else alert('Error saving order: ' + error.message)
  }

  return (
    <div>
      <div className="page-header">
        <button className="page-header-back" onClick={() => navigate(-1)}>←</button>
        <h1 className="page-title">New Order</h1>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Client */}
        <div className="card" style={{ marginBottom: '12px' }}>
          <div className="form-section-title">Client</div>
          {selectedClient ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--gray-50)', borderRadius: '8px' }}>
              <div>
                <div style={{ fontWeight: '600' }}>{selectedClient.first_name} {selectedClient.last_name}</div>
                <div style={{ fontSize: '12px', color: 'var(--gray-500)' }}>{selectedClient.email}</div>
              </div>
              <button type="button" className="btn btn-outline btn-sm" onClick={() => { setSelectedClient(null); setClientSearch('') }}>Change</button>
            </div>
          ) : (
            <div style={{ position: 'relative' }}>
              <div className="search-bar" style={{ marginBottom: clientResults.length ? '0' : '0' }}>
                <Search size={16} />
                <input value={clientSearch} onChange={e => setClientSearch(e.target.value)} placeholder="Search existing client…" />
              </div>
              {clientResults.length > 0 && (
                <div style={{ border: '1px solid var(--gray-200)', borderRadius: '8px', overflow: 'hidden', marginTop: '4px', background: 'white', boxShadow: 'var(--shadow-md)' }}>
                  {clientResults.map(c => (
                    <div key={c.id} onClick={() => { setSelectedClient(c); setClientSearch(''); setClientResults([]) }} style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--gray-100)' }}>
                      <div style={{ fontWeight: '500' }}>{c.first_name} {c.last_name}</div>
                      <div style={{ fontSize: '12px', color: 'var(--gray-400)' }}>{c.email || c.phone}</div>
                    </div>
                  ))}
                </div>
              )}
              <p style={{ fontSize: '12px', color: 'var(--gray-400)', marginTop: '6px' }}>
                Or <button type="button" onClick={() => navigate('/clients/new')} style={{ color: 'var(--gold)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '500' }}>create new client</button> first
              </p>
            </div>
          )}
        </div>

        {/* Garment */}
        <div className="card" style={{ marginBottom: '12px' }}>
          <div className="form-section-title">Garment Details</div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Type</label>
              <select className="form-select" value={form.garment_type} onChange={e => set('garment_type', e.target.value)}>
                {GARMENT_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Quantity</label>
              <input className="form-input" type="number" min="1" value={form.quantity} onChange={e => set('quantity', e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Description <span>(e.g. 2-piece navy suit, peak lapel)</span></label>
            <input className="form-input" value={form.description} onChange={e => set('description', e.target.value)} placeholder="Brief description…" />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Style Notes</label>
            <textarea className="form-textarea" value={form.style_notes} onChange={e => set('style_notes', e.target.value)} placeholder="Lapel style, buttons, pockets, lining, cuffs, vents…" rows={3} />
          </div>
        </div>

        {/* Fabric */}
        <div className="card" style={{ marginBottom: '12px' }}>
          <div className="form-section-title">Fabric</div>
          {selectedFabric ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: 'var(--gray-50)', borderRadius: '8px', marginBottom: '10px' }}>
              {selectedFabric.image_url && <PrivateImage src={selectedFabric.image_url} alt="" style={{ width: '44px', height: '44px', objectFit: 'cover', borderRadius: '6px' }} />}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '10px', color: 'var(--gold)', fontWeight: '600' }}>{selectedFabric.fabric_code}</div>
                <div style={{ fontWeight: '600' }}>{selectedFabric.name}</div>
                <div style={{ fontSize: '12px', color: 'var(--gray-400)' }}>{selectedFabric.color} · {selectedFabric.supplier}</div>
              </div>
              <button type="button" className="btn btn-outline btn-sm" onClick={() => { setSelectedFabric(null); setFabricSearch('') }}>Change</button>
            </div>
          ) : (
            <div style={{ position: 'relative', marginBottom: '10px' }}>
              <div className="search-bar">
                <Search size={16} />
                <input value={fabricSearch} onChange={e => setFabricSearch(e.target.value)} placeholder="Search fabric database…" />
              </div>
              {fabricResults.length > 0 && (
                <div style={{ border: '1px solid var(--gray-200)', borderRadius: '8px', overflow: 'hidden', marginTop: '4px', background: 'white', boxShadow: 'var(--shadow-md)' }}>
                  {fabricResults.map(f => (
                    <div key={f.id} onClick={() => { setSelectedFabric(f); setFabricSearch(''); setFabricResults([]) }} style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--gray-100)', display: 'flex', gap: '10px', alignItems: 'center' }}>
                      {f.image_url && <PrivateImage src={f.image_url} alt="" style={{ width: '36px', height: '36px', objectFit: 'cover', borderRadius: '4px' }} />}
                      <div>
                        <div style={{ fontSize: '10px', color: 'var(--gold)', fontWeight: '600' }}>{f.fabric_code}</div>
                        <div style={{ fontSize: '14px', fontWeight: '500' }}>{f.name}</div>
                        <div style={{ fontSize: '12px', color: 'var(--gray-400)' }}>{f.color} · {f.category}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Fabric Notes <span>(if not in database)</span></label>
            <textarea className="form-textarea" rows={2} value={form.fabric_notes} onChange={e => set('fabric_notes', e.target.value)} placeholder="Fabric description, supplier reference, special requirements…" />
          </div>
          <div className="form-group">
            <label className="form-label">Lining Notes</label>
            <input className="form-input" value={form.lining_notes} onChange={e => set('lining_notes', e.target.value)} placeholder="Lining color, pattern, preferences…" />
          </div>
          {/* Fabric swatch photo */}
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Fabric Swatch Photo</label>
            {fabricImageUrl ? (
              <div style={{ position: 'relative' }}>
                <PrivateImage src={fabricImageUrl} alt="Fabric swatch" style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '8px' }} />
                <button type="button" onClick={() => setFabricImageUrl('')} style={{ position: 'absolute', top: '6px', right: '6px', background: 'rgba(0,0,0,0.5)', color: 'white', border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', fontSize: '14px' }}>×</button>
              </div>
            ) : (
              <label className="photo-upload">
                <Camera size={24} className="photo-upload-icon" style={{ display: 'block', margin: '0 auto 8px' }} />
                <p className="photo-upload-text">{uploadingFabric ? 'Uploading…' : 'Tap to take or upload photo'}</p>
                <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => uploadImage(e.target.files[0], 'fabric-swatches', setFabricImageUrl, setUploadingFabric)} disabled={uploadingFabric} />
              </label>
            )}
          </div>
        </div>

        {/* Style reference photo */}
        <div className="card" style={{ marginBottom: '12px' }}>
          <div className="form-section-title">Style Reference</div>
          {styleImageUrl ? (
            <div style={{ position: 'relative' }}>
              <PrivateImage src={styleImageUrl} alt="Style reference" style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '8px' }} />
              <button type="button" onClick={() => setStyleImageUrl('')} style={{ position: 'absolute', top: '6px', right: '6px', background: 'rgba(0,0,0,0.5)', color: 'white', border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', fontSize: '14px' }}>×</button>
            </div>
          ) : (
            <label className="photo-upload">
              <Camera size={24} className="photo-upload-icon" style={{ display: 'block', margin: '0 auto 8px' }} />
              <p className="photo-upload-text">{uploadingStyle ? 'Uploading…' : 'Photo of style reference or inspiration'}</p>
              <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => uploadImage(e.target.files[0], 'order-style-refs', setStyleImageUrl, setUploadingStyle)} disabled={uploadingStyle} />
            </label>
          )}
        </div>

        {/* Handwritten scan */}
        <div className="card" style={{ marginBottom: '12px' }}>
          <div className="form-section-title">Handwritten Order Sheet</div>
          <p style={{ fontSize: '13px', color: 'var(--gray-500)', marginBottom: '10px' }}>
            Take a photo of your handwritten order/measurement sheet to attach to this order
          </p>
          {scanUrl ? (
            <div style={{ position: 'relative' }}>
              <PrivateImage src={scanUrl} alt="Order scan" style={{ width: '100%', maxHeight: '300px', objectFit: 'cover', borderRadius: '8px' }} />
              <button type="button" onClick={() => setScanUrl('')} style={{ position: 'absolute', top: '6px', right: '6px', background: 'rgba(0,0,0,0.5)', color: 'white', border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', fontSize: '14px' }}>×</button>
            </div>
          ) : (
            <label className="photo-upload">
              <Camera size={24} className="photo-upload-icon" style={{ display: 'block', margin: '0 auto 8px' }} />
              <p className="photo-upload-text" style={{ fontWeight: '500' }}>{uploadingScan ? 'Uploading…' : '📸 Scan handwritten sheet'}</p>
              <p className="photo-upload-text" style={{ fontSize: '12px', marginTop: '4px' }}>Attach the physical sheet as a backup</p>
              <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => uploadImage(e.target.files[0], 'order-scans', setScanUrl, setUploadingScan)} disabled={uploadingScan} />
            </label>
          )}
        </div>

        {/* Dates & Status */}
        <div className="card" style={{ marginBottom: '12px' }}>
          <div className="form-section-title">Dates & Status</div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Order Date</label>
              <input className="form-input" type="date" value={form.order_date} onChange={e => set('order_date', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-select" value={form.status} onChange={e => set('status', e.target.value)}>
                {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Needed By <span>(client request)</span></label>
              <input className="form-input" type="date" value={form.needed_by} onChange={e => set('needed_by', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Expected Delivery</label>
              <input className="form-input" type="date" value={form.expected_delivery} onChange={e => set('expected_delivery', e.target.value)} />
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Trunk Show City <span>(if applicable)</span></label>
            <input className="form-input" value={form.trunk_show_city} onChange={e => set('trunk_show_city', e.target.value)} placeholder="e.g. New York City" />
          </div>
        </div>

        {/* Pricing */}
        <div className="card" style={{ marginBottom: '12px' }}>
          <div className="form-section-title">Pricing</div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Price</label>
              <input className="form-input" type="number" step="0.01" value={form.price} onChange={e => set('price', e.target.value)} placeholder="0.00" />
            </div>
            <div className="form-group">
              <label className="form-label">Currency</label>
              <select className="form-select" value={form.currency} onChange={e => set('currency', e.target.value)}>
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Deposit Paid <span>(optional)</span></label>
            <input className="form-input" type="number" step="0.01" value={form.deposit_paid} onChange={e => set('deposit_paid', e.target.value)} placeholder="0.00" />
          </div>
        </div>

        {/* Notes */}
        <div className="card" style={{ marginBottom: '24px' }}>
          <div className="form-section-title">Notes</div>
          <textarea className="form-textarea" rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any additional notes about this order…" style={{ marginBottom: 0 }} />
        </div>

        <button type="submit" className="btn btn-primary btn-lg" disabled={saving} style={{ borderRadius: '12px', marginBottom: '8px' }}>
          {saving ? 'Saving…' : '✓ Save Order'}
        </button>
      </form>
    </div>
  )
}
